import { PaginationResponse } from '~/store/apis/types/Pagination';
import {
  GetActiveLoanResponseDto,
  GetLoanInvoicesResponseDto,
  GetLoanStatementResponseDto,
} from '~/store/apis/dtos/loan/loans.dto';
import { CountryCode } from '~/enums';
import { formatValueWithLocalisedCurrency } from '@qred/fe-cp-formatting-helpers';
import {
  CurrentInvoiceResponse,
  Invoice,
  validCurrentInvoiceStatuses,
} from '~/models/loans/invoice';
import { Statement } from '~/models/loans/statement';
import { Loan } from '~/models/loans/loan';
import dayjs from 'dayjs';
import { convertArrayDateToDate, formatDateSafely } from '~/helpers/utils';

const mapInvoiceToModel = (
  invoice: GetLoanInvoicesResponseDto,
  market: CountryCode
): Invoice => ({
  id: invoice.id,
  totalAmount: formatValueWithLocalisedCurrency(invoice.totalAmount, market),
  status: invoice.status,
  paidDate: formatDateSafely(invoice.paidDate, market),
  dueDate: formatDateSafely(invoice.dueDate, market),
  sentDate: invoice.sentDate,
  amortization: formatValueWithLocalisedCurrency(invoice.amortization, market),
  prolongationFee: formatValueWithLocalisedCurrency(
    invoice.prolongationFee,
    market
  ),
  monthlyFee: formatValueWithLocalisedCurrency(invoice.monthlyFee, market),
  downloadable: invoice.status !== 'UPCOMING' && invoice.sentDate !== null,
  lateDays: dayjs().diff(dayjs(invoice.dueDate), 'days'),
  daysBeforeDueDate: dayjs(invoice.dueDate).diff(dayjs(), 'days'),
  lateFee:
    invoice.lateFee > 0
      ? formatValueWithLocalisedCurrency(invoice.lateFee, market)
      : undefined,
  latePenaltyFee:
    invoice.latePenaltyFee > 0
      ? formatValueWithLocalisedCurrency(invoice.latePenaltyFee, market)
      : undefined,
  amortizationOpen:
    invoice.amortizationOpen > 0
      ? formatValueWithLocalisedCurrency(invoice.amortizationOpen, market)
      : undefined,
  monthlyFeeOpen:
    invoice.monthlyFeeOpen > 0
      ? formatValueWithLocalisedCurrency(invoice.monthlyFeeOpen, market)
      : undefined,
  paperInvoiceFee:
    invoice.paperInvoiceFee > 0
      ? formatValueWithLocalisedCurrency(invoice.paperInvoiceFee, market)
      : undefined,
  reactivationFee: invoice.reactivationFee
    ? formatValueWithLocalisedCurrency(invoice.reactivationFee, market)
    : undefined,
  feeDiscount:
    invoice.feeDiscount > 0
      ? formatValueWithLocalisedCurrency(invoice.feeDiscount, market)
      : undefined,
  amoFreeMonthExecuted: invoice.amoFreeMonthExecuted,
  nextSEPADDMonth:
    invoice.status === 'NEW'
      ? dayjs(convertArrayDateToDate(invoice?.dueDate)).add(1, 'month').toDate()
      : null,
});

export const getLoanActiveMapped = (
  response: GetActiveLoanResponseDto,
  market: CountryCode
): Loan => ({
  id: response.id,
  contractNumber: response.contractNumber,
  status: response.status,
  amount: formatValueWithLocalisedCurrency(response.amount, market),
  term: response.term,
  startDate: formatDateSafely(response.startDate, market),
  closedDate: formatDateSafely(response.closedDate, market),
  amountPaid: formatValueWithLocalisedCurrency(response.amountPaid, market),
  amountLeft: {
    label: formatValueWithLocalisedCurrency(response.amountLeft, market),
    value: response.amountLeft,
  },
  monthsProlonged: response.monthsProlonged,
  grantedByExternal: response.grantedByExternal,
  percentagePaid: Math.round(
    (response.amountPaid / (response.amountPaid + response.amountLeft)) * 100
  ),
  totalAmount: formatValueWithLocalisedCurrency(
    response.amountPaid + response.amountLeft,
    market
  ),
  isOpen: response.status === 'Open_Active' || response.status === 'Open_Late',
  isLate: response.status === 'Open_Late',
  isCollection: response.status === 'Open_In_Collection',
  amoFreeMonthsEligible: response.amoFreeMonthsEligible,
  terminationLetterSent: response.terminationLetterSent,
  paidOut: response.paidOut,
  payoutDate: formatDateSafely(response.closedDate, market),
  estimatedTermDate: formatDateSafely(response.estimatedTermDate, market),
  monthlyInterestRate: response.monthlyInterestRate,
  directDebitEnabled: response.directDebitEnabled,
  bankAccountNumber: response.bankAccountNumber,
  signer: response.signer,
  loanType: response.loanType,
});

export const getLoanCurrentInvoiceMapped = (
  response: PaginationResponse<GetLoanInvoicesResponseDto>,
  market: CountryCode
): CurrentInvoiceResponse | undefined => {
  const relevantInvoices = response.content?.filter((i) =>
    validCurrentInvoiceStatuses.includes(i.status)
  );

  if (!relevantInvoices?.length) {
    return undefined;
  }

  const hasOverdueInvoices = relevantInvoices.some(
    (i) => i.status === 'OVERDUE'
  );

  const areAllInvoicesUpcoming = relevantInvoices.every(
    (invoice) => invoice.status === 'UPCOMING'
  );

  if (hasOverdueInvoices) {
    const latestInvoices = relevantInvoices.slice(0, 8);

    return {
      invoices: latestInvoices.map((invoice) =>
        mapInvoiceToModel(invoice, market)
      ),
      hasOverdueInvoices: true,
      areAllInvoicesUpcoming: false,
    };
  }

  if (areAllInvoicesUpcoming) {
    const firstUpcomingInvoice = relevantInvoices[0];
    return {
      invoices: [mapInvoiceToModel(firstUpcomingInvoice, market)],
      areAllInvoicesUpcoming: true,
      hasOverdueInvoices: false,
    };
  }

  return {
    invoices: [mapInvoiceToModel(relevantInvoices[0], market)],
    hasOverdueInvoices: false,
    areAllInvoicesUpcoming: false,
  };
};

export const getLoanInvoicesMapped = (
  response: PaginationResponse<GetLoanInvoicesResponseDto>,
  market: CountryCode
): PaginationResponse<Invoice> => ({
  ...response,
  content: response.content.map((invoice) => ({
    ...mapInvoiceToModel(invoice, market),
    nextSEPADDMonth: (() => {
      if (invoice.status === 'NEW') {
        return dayjs(convertArrayDateToDate(invoice.dueDate))
          .add(1, 'month')
          .toDate();
      } else if (invoice.status === 'UPCOMING') {
        return dayjs(convertArrayDateToDate(invoice.dueDate)).toDate();
      } else {
        return null;
      }
    })(),
  })),
});

export const getLoanStatementMapped = (
  response: GetLoanStatementResponseDto,
  market: CountryCode
): Statement => ({
  ...response,
  earlyRepaymentAmount: formatValueWithLocalisedCurrency(
    response.earlyRepaymentAmount,
    market
  ),
});
